import { useCallback, useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import usePageMeta from "../../hooks/usePageMeta";
import { cleanRegistration, formatUkRegistration, isValidUkRegistration } from "../../utils/registration";
import { isValidVin, normaliseVin } from "../../../lib/vehicle/vin.js";
import { completeAssuranceByBrand, packagesFor } from "../../config/pricing.js";

const packages = packagesFor("servicelad");
import { FadeIn, PrimaryCta } from "./PrimaryCta";
import { Icon } from "./Icon";
import SlCloudNav from "./SlCloudNav";
import SlCloudFooter from "./SlCloudFooter";
import SlPackagesModal from "./SlPackagesModal";
import SlReportShowcase from "./SlReportShowcase";
import SlBrandsCovered from "./SlBrandsCovered";
import RecentRegChips from "../../components/RecentRegChips";
import { goToLookup } from "./goToLookup";

const featureCards = [
  { icon: "history", title: "Service timeline", body: "Dated manufacturer records in order, so gaps and patterns are easy to spot." },
  { icon: "gauge", title: "Mileage trail", body: "Compare mileage across services before you trust the odometer alone." },
  { icon: "shield", title: "Risk checks", body: "Stolen, write-off and finance markers sit beside the service story." },
  { icon: "bell-ring", title: "V5C watching", body: "Optional logbook alerts when DVLA reissues the keeper document." },
  { icon: "file-search", title: "Clear reports", body: "A calm digital summary you can keep when buying, selling or owning." },
  { icon: "car", title: "Free vehicle page", body: "Tax, MOT and basics first. Paid reports unlock the deeper history." },
];

const HERO_CHECK_ITEMS = [
  "Finance check",
  "Write-off",
  "Stolen check",
  "Import/export",
  "Valuation",
  "Service history",
];

const TIMELINE_EVENTS = [
  {
    id: "service-done",
    date: "Oct 2024",
    title: "Service Done",
    detail: "BMW Luton LU1 XXX",
    type: "success",
    icon: "check-circle-2",
  },
  {
    id: "service-missed",
    date: "May 2024",
    title: "Service Missed",
    detail: "14 months between logs",
    type: "alert",
    icon: "alert-triangle",
  },
  {
    id: "oil-change",
    date: "Nov 2023",
    title: "Oil & Brake Service",
    detail: "Greater London NW8 XXX",
    type: "primary",
    icon: "wrench",
  },
  {
    id: "mileage-verified",
    date: "Sep 2023",
    title: "Mileage Verified",
    detail: "24,180 mi · BMW Bristol BS2 XXX",
    type: "success",
    icon: "gauge",
  },
  {
    id: "v5c-update",
    date: "Jan 2023",
    title: "V5C Logbook Updated",
    detail: "DVLA Swansea SA6 XXX",
    type: "neutral",
    icon: "bell-ring",
  },
];

export default function ServiceLadLanding({ brand }) {
  const [mode, setMode] = useState("reg"); // "reg" | "vin"
  const [vrm, setVrm] = useState("");
  const [vin, setVin] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupReg, setLookupReg] = useState("");
  const [eventIndex, setEventIndex] = useState(0);
  const [checkIndex, setCheckIndex] = useState(0);
  const [packagesOpen, setPackagesOpen] = useState(false);
  const reduce = useReducedMotion();
  const closePackagesModal = useCallback(() => setPackagesOpen(false), []);

  usePageMeta({
    title: `Cheapest Full HPI Check £7.95, Free MOT and Tax | ${brand.name}`,
    description: "A clearer view of the records behind your car, so every service, mileage reading and change is easy to follow.",
    canonical: `https://${brand.domain}/`,
  });

  useEffect(() => {
    if (reduce) return;
    const intervalId = window.setInterval(() => {
      setCheckIndex((prev) => (prev + 1) % HERO_CHECK_ITEMS.length);
    }, 1400);
    return () => window.clearInterval(intervalId);
  }, [reduce]);

  useEffect(() => {
    if (reduce) return;
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    let intervalId;

    const start = () => {
      window.clearInterval(intervalId);
      const ms = mobileQuery.matches ? 1200 : 3200;
      intervalId = window.setInterval(() => {
        setEventIndex((prev) => (prev + 1) % TIMELINE_EVENTS.length);
      }, ms);
    };

    start();
    mobileQuery.addEventListener("change", start);
    return () => {
      window.clearInterval(intervalId);
      mobileQuery.removeEventListener("change", start);
    };
  }, [reduce]);

  useEffect(() => {
    // Coming back with the browser back button restores the page from memory
    // with the lookup modal still open; close it so the form is usable again.
    const onShow = (event) => { if (event.persisted) { setLookingUp(false); setLookupReg(""); } };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    requestAnimationFrame(() => {
      if (hash === "lookup") {
        goToLookup();
        return;
      }
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const setLookupMode = (next) => {
    if (lookingUp || next === mode) return;
    setError("");
    setNote("");
    setMode(next);
    if (next === "vin") setVrm("");
    else setVin("");
  };

  const resolveVinAndGo = async (nextVin) => {
    setError("");
    setNote("");
    setLookupReg(nextVin);
    setLookingUp(true);
    try {
      const response = await fetch(`/api/mot-lookup?vin=${encodeURIComponent(nextVin)}`);
      const payload = await response.json().catch(() => ({}));
      if (response.status === 429) {
        setLookingUp(false);
        setError(payload.error || "Too many requests. Please try again shortly.");
        return;
      }
      if (!response.ok || !payload.ok) {
        setLookingUp(false);
        setError(payload.error || "We could not find that VIN. Check it and try again.");
        return;
      }
      if (payload.registration) {
        window.location.href = `/vehicle/${String(payload.registration).toLowerCase()}`;
        return;
      }
      window.location.href = `/vehicle/vin/${nextVin}`;
    } catch {
      setLookingUp(false);
      setError("Lookup failed. Check your connection and try again.");
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (lookingUp) return;

    if (mode === "vin") {
      const nextVin = normaliseVin(vin);
      if (!isValidVin(nextVin)) {
        setError("Enter a valid 17-character VIN");
        return;
      }
      void resolveVinAndGo(nextVin);
      return;
    }

    const asVin = normaliseVin(vrm);
    if (isValidVin(asVin)) {
      void resolveVinAndGo(asVin);
      return;
    }
    if (asVin.length === 17) {
      setError("Enter a valid 17-character VIN");
      return;
    }

    const clean = cleanRegistration(vrm);
    if (!isValidUkRegistration(clean)) {
      setError("Enter a valid UK registration to continue.");
      return;
    }
    setError("");
    setNote("");
    setLookupReg(formatUkRegistration(clean));
    setLookingUp(true);
    window.location.href = `/vehicle/${clean.toLowerCase()}`;
  };

  const currentBadge = TIMELINE_EVENTS[eventIndex];
  const nextBadge = TIMELINE_EVENTS[(eventIndex + 1) % TIMELINE_EVENTS.length];

  return (
    <div className="sl-cloud sl-cloud-home relative min-h-screen bg-background text-foreground font-body" aria-busy={lookingUp}>
      <div className="atmosphere" aria-hidden />
      <SlCloudNav brand={brand} />

      {lookingUp ? (
        <div className="sl-lookup-busy" role="status" aria-live="polite" aria-atomic="true">
          <div className="sl-lookup-busy-card">
            {!reduce ? <div className="sl-lookup-busy-glow" aria-hidden="true" /> : null}
            <span className={`sl-lookup-busy-plate${lookupReg.length > 8 ? " is-vin" : ""}`}>{lookupReg}</span>
            <p className="sl-lookup-busy-title">Looking up your car…</p>
            <p className="sl-lookup-busy-sub">Fetching tax, MOT and vehicle details</p>
            <span className="sl-lookup-busy-ring" aria-hidden="true" />
          </div>
        </div>
      ) : null}

      <main id="top">
        <section className="relative z-10 mx-auto max-w-4xl px-6 pb-2 pt-6 text-center md:pt-10">
          {!reduce ? (
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-8 h-56 w-56 -translate-x-1/2 rounded-full bg-primary/30 blur-3xl"
            />
          ) : null}
          <p className="relative mx-auto block w-full text-center font-heading text-xs font-medium uppercase tracking-[0.22em] pl-[0.22em] text-primary">Ownership clarity</p>
          <h1 className="relative mx-auto mt-3 w-full text-center font-heading text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
            <span className="block">
              Know what
              <br className="md:hidden" />
              {" "}
              changed.
            </span>
            <span className="block text-primary">
              Stay ahead of
              <br className="md:hidden" />
              {" "}
              your car.
            </span>
          </h1>

          <div id="lookup" className="relative mx-auto mt-4 flex w-fit max-w-full scroll-mt-28 flex-col items-center gap-3 md:mt-5">
            <div className="sl-lookup-toolbar">
              <div className="sl-lookup-toggle" role="radiogroup" aria-label="Lookup type">
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "reg"}
                  disabled={lookingUp}
                  className={`sl-lookup-toggle-option${mode === "reg" ? " is-active" : ""}`}
                  onClick={() => setLookupMode("reg")}
                >
                  REG
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "vin"}
                  disabled={lookingUp}
                  className={`sl-lookup-toggle-option${mode === "vin" ? " is-active" : ""}`}
                  onClick={() => setLookupMode("vin")}
                >
                  VIN
                </button>
              </div>
              <RecentRegChips
                brandKey="sl"
                variant="sl"
                onSelect={(reg) => {
                  const clean = cleanRegistration(reg);
                  setLookupMode("reg");
                  setVrm(clean);
                  setVin("");
                  setError("");
                  setNote("");
                  setLookupReg(formatUkRegistration(clean));
                  setLookingUp(true);
                  window.location.href = `/vehicle/${clean.toLowerCase()}`;
                }}
              />
            </div>
            <form
              onSubmit={submit}
              className="relative flex w-fit max-w-full flex-row items-center justify-center gap-1.5"
            >
              <label htmlFor="sl-vrm" className="sr-only">
                {mode === "vin" ? "Vehicle identification number" : "UK registration"}
              </label>
              <div className={`sl-plate-field${mode === "vin" || vrm.length > 7 ? " is-vin" : ""}`}>
                {mode === "reg" && vrm.length <= 7 ? (
                  <span className="sl-plate-band" aria-hidden="true">
                    <img src="/gb-flag.svg" alt="" width="22" height="14" />
                    UK
                  </span>
                ) : null}
                <div className="sl-plate-type">
                  {mode === "reg" && !vrm ? <span className="sl-plate-ph" aria-hidden="true">ENTER REG</span> : null}
                  {mode === "vin" && !vin ? <span className="sl-plate-ph" aria-hidden="true">ENTER VIN</span> : null}
                  <input
                    id="sl-vrm"
                    name={mode === "vin" ? "vin" : "vrm"}
                    value={mode === "vin" ? vin : vrm}
                    maxLength={17}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder=""
                    disabled={lookingUp}
                    className="sl-plate-input"
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (mode === "vin") {
                        setVin(normaliseVin(raw));
                      } else {
                        const candidate = normaliseVin(raw);
                        if (candidate.length > 7) setVrm(candidate);
                        else setVrm(cleanRegistration(raw));
                      }
                      if (error) setError("");
                      if (note) setNote("");
                    }}
                  />
                </div>
              </div>
              <button
                type="submit"
                className={`sl-plate-go${lookingUp ? " is-busy" : ""}`}
                disabled={lookingUp}
                aria-label={lookingUp ? "Looking up vehicle" : mode === "vin" ? "Look up VIN" : "Check vehicle"}
              >
                {lookingUp ? <span className="sl-plate-go-spinner" aria-hidden="true" /> : <Icon name="arrow-right" size={22} />}
              </button>
            </form>
          </div>
          {error ? <p className="relative mt-2 text-sm text-red-600" role="alert">{error}</p> : null}
          {note ? <p className="relative mt-2 max-w-md text-sm text-muted-foreground" role="status">{note}</p> : null}

          <div className="sl-hero-offer">
            <p className="sl-hero-offer-kicker">Start free</p>
            <p className="sl-hero-offer-free">
              Road tax, MOT and specs on every search.
            </p>
            <div className="sl-hero-offer-rule" aria-hidden="true" />
            <div className="sl-hero-offer-paid-row">
              <p className="sl-hero-offer-paid">
                Full HPI check from £{packages.find((plan) => plan.key === "risk")?.plate}
              </p>
              <div className="sl-hero-offer-checks" aria-live="polite">
                <span key={checkIndex} className="sl-hero-offer-check">
                  {HERO_CHECK_ITEMS[checkIndex]}
                </span>
                <span className={`sl-hero-offer-included${HERO_CHECK_ITEMS[checkIndex] === "Service history" ? " is-extra" : ""}`}>
                  <Icon name="check" size={12} />
                  {/* Service history is only in the dearer check, so say which. */}
                  {HERO_CHECK_ITEMS[checkIndex] === "Service history"
                    ? `In the £${packages.find((plan) => plan.key === "complete")?.plate} check`
                    : "Included"}
                </span>
              </div>
            </div>
            <p className="sl-hero-offer-service">
              Full manufacturer service history also available for cars from 2012.
            </p>
          </div>
        </section>

        <FadeIn>
          <section className="relative z-10 mx-auto max-w-6xl px-6 pb-4 pt-2">
            <div className="sl-cls-card">
              <div className="sl-cls-card-copy">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Stay ahead</p>
                <p className="mt-2 font-heading text-xl font-semibold tracking-tight md:text-2xl">Every mile leaves a clue. We make it clear.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Free tax and MOT first. Then unlock risk checks, manufacturer service history where available, and optional V5C alerts while you own the car.
                </p>
                <div className="sl-cls-card-actions">
                  <button
                    type="button"
                    className="sl-price-buy"
                    onClick={goToLookup}
                  >
                    Buy
                  </button>
                  <button
                    type="button"
                    className="sl-what-i-get"
                    onClick={() => setPackagesOpen(true)}
                  >
                    What I get?
                  </button>
                </div>
              </div>
              <div className="sl-cls-card-visual">
                <div className="sl-live-badges" aria-live="polite">
                  <div key={currentBadge.id} className={`sl-live-badge is-${currentBadge.type} is-primary-card`}>
                    <div className="sl-live-badge-icon">
                      <Icon name={currentBadge.icon} size={15} />
                    </div>
                    <div className="sl-live-badge-body">
                      <div className="sl-live-badge-top">
                        <span className="sl-live-badge-date">{currentBadge.date}</span>
                        <span className="sl-live-badge-dot">·</span>
                        <span className="sl-live-badge-title">{currentBadge.title}</span>
                      </div>
                      <span className="sl-live-badge-sub">{currentBadge.detail}</span>
                    </div>
                  </div>
                  <div key={nextBadge.id} className={`sl-live-badge is-${nextBadge.type} is-secondary`} aria-hidden="true">
                    <div className="sl-live-badge-icon">
                      <Icon name={nextBadge.icon} size={15} />
                    </div>
                    <div className="sl-live-badge-body">
                      <div className="sl-live-badge-top">
                        <span className="sl-live-badge-date">{nextBadge.date}</span>
                        <span className="sl-live-badge-dot">·</span>
                        <span className="sl-live-badge-title">{nextBadge.title}</span>
                      </div>
                      <span className="sl-live-badge-sub">{nextBadge.detail}</span>
                    </div>
                  </div>
                </div>
                <picture>
                  <source srcSet="/service-lad-bmw.webp" type="image/webp" />
                  <img
                    src="/service-lad-bmw.png"
                    alt="ServiceLad vehicle check illustration"
                    width={700}
                    height={360}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
            </div>
          </section>
        </FadeIn>

        <FadeIn>
          <section id="features" className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-6 py-12">
            <div className="mb-8 mx-auto max-w-2xl text-center">
              <p className="font-heading text-xs font-medium uppercase tracking-[0.22em] text-primary">Features</p>
              <h2 className="mt-3 font-heading text-balance text-3xl font-semibold tracking-tight md:text-4xl">Built for keepers who want the full picture.</h2>
            </div>
            <div className="space-y-3">
              <div className="relative flex flex-col items-center overflow-hidden rounded-2xl border border-border bg-card p-6 text-center">
                <div aria-hidden className="pointer-events-none absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />
                <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Icon name="sparkles" />
                </div>
                <p className="relative mt-3 font-heading text-xl font-semibold leading-tight tracking-tight">Free first, then the deep dive</p>
                <p className="relative mt-2 max-w-md text-sm text-muted-foreground">
                  Every search opens a free vehicle page. Paid reports add the checks that matter when money is on the line.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {featureCards.slice(0, 3).map((f) => (
                  <div key={f.title} className="flex flex-col items-center rounded-xl border border-border bg-card p-4 text-center">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-background text-primary">
                      <Icon name={f.icon} size={16} />
                    </div>
                    <p className="mt-2 font-heading text-sm font-semibold">{f.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{f.body}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {featureCards.slice(3).map((f) => (
                  <div key={f.title} className="flex flex-col items-center rounded-xl border border-border bg-card p-4 text-center">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-background text-primary">
                      <Icon name={f.icon} size={16} />
                    </div>
                    <p className="mt-2 font-heading text-sm font-semibold">{f.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{f.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeIn>

        <FadeIn>
          <section id="pricing" className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-6 py-12">
            <div className="mb-8 max-w-2xl">
              <p className="font-heading text-xs font-medium uppercase tracking-[0.22em] text-primary">Pricing</p>
              <h2 className="mt-3 font-heading text-balance text-3xl font-semibold tracking-tight md:text-4xl">One-off reports. No subscription.</h2>
            </div>
            <div className="grid gap-3 md:grid-cols-[1.4fr_1fr] md:grid-rows-2">
              {packages.filter((p) => p.featured).map((plan) => (
                <div key={plan.key} className="flex flex-col rounded-2xl border border-primary bg-card p-5 md:row-span-2">
                  <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-primary">Recommended</p>
                  <p className="mt-2 font-heading text-4xl font-semibold tracking-tight">
                    £{plan.plate}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">one-off</span>
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{plan.name}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{plan.copy}</p>
                  <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                    {plan.items.map((item) => (
                      <li key={item} className="flex gap-2">
                        <Icon name="check" size={14} className="mt-0.5 shrink-0 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-muted-foreground">{completeAssuranceByBrand.servicelad}</p>
                  <div className="mt-auto pt-4">
                    <PrimaryCta onClick={goToLookup}>
                      {plan.button}
                    </PrimaryCta>
                  </div>
                </div>
              ))}
              {packages.filter((p) => !p.featured).map((plan) => (
                <div key={plan.key} className="flex flex-col justify-center rounded-xl border border-border bg-card p-4">
                  <div>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{plan.name}</p>
                      <p className="font-heading font-semibold">
                        £{plan.plate}
                        <span className="ml-1 text-[10px] font-normal text-muted-foreground">one-off</span>
                      </p>
                    </div>
                    <ul className="mt-2 space-y-1 text-[10.5px] leading-relaxed text-muted-foreground">
                      {plan.items.map((item) => (
                        <li key={item} className="flex gap-1.5">
                          <Icon name="check" size={12} className="mt-0.5 shrink-0 text-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    type="button"
                    className="sl-price-buy"
                    onClick={goToLookup}
                  >
                    Buy
                  </button>
                </div>
              ))}
              <div className="flex flex-col justify-center rounded-xl border border-border bg-card p-4">
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">V5C alerts</p>
                    <p className="font-heading font-semibold">
                      £1.49
                      <span className="ml-1 text-[10px] font-normal text-muted-foreground">from</span>
                    </p>
                  </div>
                  <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                    Optional logbook monitoring for 3, 6 or 12 months while you own the car.
                  </p>
                  <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
                    We watch for a DVLA reissue of the V5C keeper document and email you if that happens. That can matter after you buy, if you lend the car, or if it is advertised without you knowing.
                  </p>
                  <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
                    Choose a term that fits how long you want cover. One-off payment only, no subscription and no auto-renewal.
                  </p>
                </div>
                <a href="/v5c-alerts" className="sl-price-buy">
                  Buy
                </a>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              <a href="/cheapest-hpi-check" className="font-semibold text-primary underline-offset-2 hover:underline">Compare HPI check prices</a>
            </p>
          </section>
        </FadeIn>

        <FadeIn>
          <SlReportShowcase />
        </FadeIn>

        <FadeIn>
          <SlBrandsCovered />
        </FadeIn>

        <FadeIn>
          <section className="relative z-10 mx-auto max-w-6xl px-6 py-12">
            <div className="rounded-3xl border border-border bg-card p-10 text-center">
              <h2 className="font-heading text-balance text-3xl font-semibold tracking-tight md:text-4xl">Ready to check the next registration?</h2>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Start free on the vehicle page. Add a paid report or V5C alerts only when you need them.</p>
              <div className="mt-6 flex justify-center">
                <PrimaryCta onClick={goToLookup}>
                  Check a vehicle
                </PrimaryCta>
              </div>
            </div>
          </section>
        </FadeIn>
      </main>

      <SlCloudFooter brand={brand} />

      <SlPackagesModal
        open={packagesOpen}
        onClose={closePackagesModal}
        brand={brand}
        initialReg={vrm}
      />
    </div>
  );
}
